import { FunctionComponent, memo, useEffect, useRef, useState } from 'react';
import { produceClipsChunked } from '../../services/AIClipProducer';
import { Speaker } from '../content-components/SpeakerSelector';
import { 
  extractSpeakerLabelsFromTranscriptElements, 
  fetchSpeakerVoiceMappings, 
  fetchDatoSpeakers, 
  buildSpeakersFromMappings, 
  filterElementsBySpeakers,
  createSpeakerFilteredTranscript 
} from '@src/utility/speakerUtils';

import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  useStore,
  useTemplatedPrompts,
  useTranscription,
} from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';

type ClipProducerProps = {
  ClipProducerUI: FunctionComponent<ClipProducerUIProps>;
  useFinalOrOriginalVideo?: boolean;
  supportedPresets: { title: string; category: string }[] | null;
  enableSpeakerSelection?: boolean; // Added to control speaker selection feature
};

export type ClipProducerUIProps = {
  supportedPresets: { title: string; category: string }[];
  isProducing: boolean;
  retryCount: number;
  selectedDuration: number | null;
  setSelectedDuration: (duration: number | null) => void;
  selectedPresets: string[];
  setSelectedPresets: (presets: string[]) => void;
  onPresetClick: (preset: string) => void;
  customPresetText: string;
  setCustomPresetText: (text: string) => void;
  selectedSocials: string[];
  setSelectedSocials: (socials: string[]) => void;
  producedClips: any[] | null;
  showModal: boolean;
  isProducingDisable: boolean;
  onProduceButtonClick: () => void;
  closeProducingModal: () => void;
  // New speaker selection props
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  setSelectedSpeakers: (speakers: Speaker[]) => void;
  useSpeakerFilter: boolean;
  setUseSpeakerFilter: (useFilter: boolean) => void;
  isLoadingSpeakers: boolean;
  showSpeakerSelector: boolean;
};

const AIClipProducer = memo(
  (props: ClipProducerProps) => {
    const videoCreator = useVideoCreatorStore();
    const rootStore = useStore();
    const transcriptionStore = useTranscription();
    const { userIdentityStore } = useStore();
    const flags = useFlagsCombination(userIdentityStore.currentIdentity?.role as any);
    const { ClipProducerUI, useFinalOrOriginalVideo = false, enableSpeakerSelection = false } = props;
    
    // Check if transcript editor is enabled via feature flag
    const isTranscriptEditorEnabled = flags.enableTranscriptEditor;
    // Determine if we should show the speaker selector based on props and feature flag
    const showSpeakerSelector = enableSpeakerSelection && isTranscriptEditorEnabled;
    
    const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
    const [selectedSocials, setSelectedSocials] = useState<string[]>([]);
    const [selectedDuration, setSelectedDuration] = useState<number | null>(
      null,
    );
    const [isProducing, setIsProducing] = useState(false);
    const [producedClips, setProducedClips] = useState<any[] | null>(null);
    const producerAbortController = useRef<{
      id: string;
      aborted: boolean;
    } | null>(null);
    const [showModal, setShowModal] = useState(false);

    // Speaker selection state
    const [speakers, setSpeakers] = useState<Speaker[]>([]);
    const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);
    const [isLoadingSpeakers, setIsLoadingSpeakers] = useState(false);
    const [useSpeakerFilter, setUseSpeakerFilter] = useState(false);

    const MAX_RETRIES = 3;

    const [presetInputValue, setPresetInputValue] = useState<string>('');

    const [retryCount, setRetryCount] = useState<number>(0);
    const retryCountRef = useRef(retryCount);
    retryCountRef.current = retryCount;
    
    // Load speaker data when component mounts if speaker selection is enabled
    useEffect(() => {
      // Skip if speaker selection is disabled or no story ID
      if (!showSpeakerSelector || !videoCreator.story?.id) return;
      
      const loadSpeakers = async () => {
        setIsLoadingSpeakers(true);
        try {
          // Extract speaker labels from transcript
          const transcriptElements = transcriptionStore.getFinalTranscriptionElements();
          const speakerLabels = extractSpeakerLabelsFromTranscriptElements(transcriptElements);
          
          if (speakerLabels.length === 0) {
            // No speakers found in transcript
            setIsLoadingSpeakers(false);
            return;
          }
          
          // Fetch mappings and Dato speakers
          const [speakerMappings, datoSpeakers] = await Promise.all([
            fetchSpeakerVoiceMappings(videoCreator.story!.id),
            fetchDatoSpeakers(videoCreator.story!.id, videoCreator.datoClientStore)
          ]);
          
          // Build the complete speaker list
          const speakerList = buildSpeakersFromMappings(
            speakerLabels,
            speakerMappings,
            datoSpeakers
          );
          
          setSpeakers(speakerList);
          // Default to selecting all speakers
          setSelectedSpeakers(speakerList);
        } catch (error) {
          console.error('Error loading speaker data:', error);
        } finally {
          setIsLoadingSpeakers(false);
        }
      };
      
      loadSpeakers();
    }, [videoCreator.story?.id, showSpeakerSelector]);

    const onPresetClick = (preset: string) => {
      if (selectedPresets.includes(preset)) {
        setSelectedPresets((prev) => prev.filter((p) => p !== preset));
      } else {
        setSelectedPresets((prev) => [preset]); //  [...prev, preset]);
        setPresetInputValue('');
      }
    };

    const closeProducingModal = () => {
      if (producerAbortController.current && isProducing) {
        producerAbortController.current.aborted = true;
      }
      setProducedClips(null);
      setIsProducing(false);
      setShowModal(false);
    };

    const isProducingDisabled = () => {
      return (
        !(selectedPresets.length || presetInputValue) ||
        !selectedSocials.length ||
        isProducing ||
        !videoCreator.story?.originalVideo ||
        // Add condition to check if speaker filter is active but no speakers selected
        (useSpeakerFilter && selectedSpeakers.length === 0)
      );
    };

    const onProduceButtonClick = async () => {
      if (
        !(selectedPresets.length || presetInputValue) ||
        !selectedSocials.length
      ) {
        return;
      }
      setIsProducing(true);
      setShowModal(true);
      producerAbortController.current = {
        id: Date.now().toString(),
        aborted: false,
      };

      const finalVideo = videoCreator.story?.finalVideo;

      if (
        useFinalOrOriginalVideo &&
        finalVideo?.id &&
        finalVideo.videoFilePrimary?.video
      ) {
        await videoCreator.loadVideoWithoutRendering(finalVideo.id, true);
        videoCreator.duration = finalVideo.videoFilePrimary!.video.duration;
      } else if (useFinalOrOriginalVideo) {
        await videoCreator.createNewVideoFromSource();
        videoCreator.duration = parseFloat(videoCreator.originalVideoDuration);
      }

      // Get original transcript elements
      const originalElements = videoCreator.currentVideo?.videoFilePrimary
        ? transcriptionStore.getFinalTranscriptionElements()
        : transcriptionStore.originalTranscription?.elements || [];
      
      // Apply speaker filtering if enabled
      let transcriptElements = originalElements;
      let transcriptionText = videoCreator.videoTranscriptionProcessor.getFinalTranscriptionText();
      let submitTranscriptionText = transcriptionText;

      if (useSpeakerFilter && selectedSpeakers.length > 0) {
        // Use our utility function to create a filtered transcript with speaker attribution
        const filteredTranscript = createSpeakerFilteredTranscript(originalElements, selectedSpeakers);
        submitTranscriptionText = filteredTranscript.text;
      }

      while (retryCountRef.current < MAX_RETRIES) {
        try {
          const matchedParts = await produceClipsChunked({
            videoCreator: videoCreator,
            originalVideo:
              videoCreator.currentVideo?.videoFilePrimary ||
              videoCreator.story!.originalVideo!,
            submitTranscriptionText: submitTranscriptionText,
            transcriptionText: transcriptionText,
            transcriptionElements: transcriptElements,
            originalDuration: videoCreator.duration,
            duration: selectedDuration,
            theme: selectedPresets[0] || presetInputValue,
            customTheme: !!presetInputValue,
            platform: selectedSocials[0],
            onClipsProduced: (clips: any) => {
              setProducedClips((prevClips) => {
                return clips.length !== prevClips?.length
                  ? [...clips]
                  : prevClips;
              });
            },
            abortToken: producerAbortController.current,
            rootStore: rootStore,
            speakers: useSpeakerFilter ? selectedSpeakers : [], // Pass selected speakers for name lookup
          });
          console.log('ALL PRODUCED CLIPS', matchedParts);
          if (!matchedParts?.length) {
            setRetryCount((prev) => prev + 1);
          } else {
            setProducedClips(matchedParts);
            break;
          }
        } catch (err: any) {
          console.error(err);
          if (err.message === 'Aborted') {
            return;
          }
          setRetryCount((prev) => prev + 1);
        }
      }
      producerAbortController.current = null;
      setRetryCount(0);
      setIsProducing(false);
    };

    return (
      <ClipProducerUI
        supportedPresets={props.supportedPresets || []}
        isProducing={isProducing}
        retryCount={retryCount}
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
        selectedPresets={selectedPresets}
        setSelectedPresets={setSelectedPresets}
        onPresetClick={onPresetClick}
        customPresetText={presetInputValue}
        setCustomPresetText={setPresetInputValue}
        selectedSocials={selectedSocials}
        setSelectedSocials={setSelectedSocials}
        producedClips={producedClips}
        showModal={showModal}
        isProducingDisable={isProducingDisabled()}
        onProduceButtonClick={onProduceButtonClick}
        closeProducingModal={closeProducingModal}
        // New speaker selection props
        speakers={speakers}
        selectedSpeakers={selectedSpeakers}
        setSelectedSpeakers={setSelectedSpeakers}
        useSpeakerFilter={useSpeakerFilter}
        setUseSpeakerFilter={setUseSpeakerFilter}
        isLoadingSpeakers={isLoadingSpeakers}
        showSpeakerSelector={showSpeakerSelector}
      />
    );
  },
  (oldProps, newProps) => {
    return (
      oldProps.supportedPresets?.length === newProps.supportedPresets?.length
    );
  },
);

export default AIClipProducer;
